import { useCallback, useEffect, useMemo } from 'react';
import { useChannelFilters } from './use-channel-filters';
import { useChannelModals } from './use-channel-modals';
import { useChannelPagination } from './use-channel-pagination';
import { useChannelList } from './queries';

/**
 * 채널 페이지 전역 컨트롤러
 *
 * @description
 * - 필터, 페이지네이션, 모달 상태를 한 곳에서 관리합니다.
 * - `useChannelList`는 기본적으로 목 데이터를 사용하므로,
 *   실제 API 연동 시 `useChannelList` 호출부의 두 번째 인자로
 *   `fetcher: channelService.getChannelList` 형태의 옵션을 전달하세요.
 * - API 연동 후에는 이 훅이 반환하는 프로퍼티를 그대로 페이지/테이블에 넘겨주면 됩니다.
 */
export function useChannelController() {
  const filters = useChannelFilters();
  const modals = useChannelModals();
  const {
    currentPage,
    limit,
    totalPages,
    setTotal,
    goToPage,
    handleNextPage,
    handlePrevPage,
    resetPagination,
  } = useChannelPagination({
    initialLimit: 10,
  });

  /**
   * 실제 API 연동 시 두 번째 인자로 `{ fetcher: channelService.getChannelList }`
   * 형태의 옵션을 전달하고, 서비스 응답에서 data만 추출하도록 래핑하면 됩니다.
   */
  const channelQuery = useChannelList({
    page: currentPage,
    limit,
    searchTerm: filters.searchTerm,
    planFilter: filters.selectedPlan,
  });

  const channels = useMemo(() => channelQuery.data?.channels ?? [], [channelQuery.data?.channels]);

  useEffect(() => {
    setTotal(channelQuery.data?.total ?? 0);
  }, [channelQuery.data?.total, setTotal]);

  const handleSearch = useCallback(() => {
    filters.executeSearch();
    resetPagination();
  }, [filters, resetPagination]);

  const handlePlanChange = useCallback(
    (plan: typeof filters.selectedPlan) => {
      // 필터 적용 시 현재 입력창(searchInput)을 기준으로 검색어를 동기화하여
      // 이전 검색어가 남아 API에 전달되지 않도록 한다.
      filters.executeSearch();
      filters.setSelectedPlan(plan);
      resetPagination();
    },
    [filters, resetPagination]
  );

  return {
    // 데이터
    channels,
    isLoading: channelQuery.isLoading,
    isFetching: channelQuery.isFetching,
    error: channelQuery.error,

    // 필터
    searchInput: filters.searchInput,
    searchTerm: filters.searchTerm,
    selectedPlan: filters.selectedPlan,
    setSearchInput: filters.setSearchInput,
    setSelectedPlan: handlePlanChange,

    // 페이지네이션
    currentPage,
    totalPages,
    goToPage,
    handleNextPage,
    handlePrevPage,

    // 검색
    handleSearch,

    // 모달 액션
    openDetailModal: modals.openDetailModal,
    openApplyPlanWithChannel: modals.openApplyPlanWithChannel,
  };
}

